import React from 'react';
import { cn, IconButton, TextField } from '@zep-us/zus';
import { ArrowUpIcon } from '@zep-us/zus/icons';

// CSS Houdini @property와 keyframes 정의 (모듈 스코프에서 한 번만 생성)
const GRADIENT_STYLES = `
  @property --gradient-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
  @keyframes rotateGradient {
    to {
      --gradient-angle: 360deg;
    }
  }
`;
type GradientStrokeInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
};
export const GradientStrokeInput: React.FC<GradientStrokeInputProps> = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  maxLength,
  disabled
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      onSubmit();
    }
  };
  return <>
      {/* CSS Houdini 정의 */}
      <style dangerouslySetInnerHTML={{
      __html: GRADIENT_STYLES
    }} />

      <div className="relative w-full">
        {/* 흐르는 그라디언트 테두리 */}
        <div className="relative rounded-[38px] p-[2px]">
          {/* 동적 그라디언트 테두리 (fallback: 정적) */}
          <div className="absolute inset-0 rounded-[38px]" style={{
          // Fallback: 정적 그라디언트 (Firefox 등)
          background: 'linear-gradient(135deg, #9ED4FF 0%, #ffffff 40%, #AF70FF 70%, #8478FF 100%)',
          // Modern browsers: 밝은 하이라이트로 회전 효과 뚜렷하게
          backgroundImage: 'conic-gradient(from var(--gradient-angle, 0deg), #9ED4FF 0deg, #ffffff 90deg, #AF70FF 180deg, #8478FF 270deg, #9ED4FF 360deg)',
          animation: 'rotateGradient 3s linear infinite',
          boxShadow: '0px 4px 8px 0px rgba(164,155,255,0.35), 0px 0px 6px 0px rgba(158,212,255,0.3)'
        }} />

          {/* TextField with custom styling */}

          <TextField.Root required disabled={disabled} data-sentry-element="unknown" data-sentry-source-file="GradientStrokeInput.tsx">
            <TextField.Input value={value} onChange={handleChange} onKeyDown={handleKeyDown} placeholder={placeholder} maxLength={maxLength} className="text-heading-sm-bold tablet-sm:text-heading-md-bold" wrapperClassName="h-[50px] rounded-[36px] border-none bg-white py-xs pl-lg pr-xs focus-within:ring-0 tablet-sm:h-[76px] tablet-sm:pl-2xl tablet-sm:pr-md" leftSlot={<GradientSparkleIcon />} rightSlot={<IconButton variant="contained" intent="strong" size="lg" rounded={'full'} onClick={onSubmit} disabled={disabled} className={cn({
            hidden: !value.trim()
          })}>
                  <ArrowUpIcon size="md" />
                </IconButton>} data-sentry-element="unknown" data-sentry-source-file="GradientStrokeInput.tsx" />
          </TextField.Root>
        </div>
      </div>
    </>;
};

//FIXME: 공통 Icon 컴포넌트 추출 여부 확정
const GradientSparkleIcon = () => {
  return <svg className="size-[24px] shrink-0 tablet-sm:size-[28px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none" data-sentry-element="svg" data-sentry-component="GradientSparkleIcon" data-sentry-source-file="GradientStrokeInput.tsx">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6207 5.25016C11.3206 5.25016 11.8879 5.80981 11.8879 6.50016C11.8879 8.15777 12.5555 9.74748 13.7438 10.9196C14.932 12.0917 16.5437 12.7502 18.2241 12.7502C18.924 12.7502 19.4914 13.3098 19.4914 14.0002C19.4914 14.6905 18.924 15.2502 18.2241 15.2502C16.5437 15.2502 14.932 15.9086 13.7438 17.0807C12.5555 18.2528 11.8879 19.8426 11.8879 21.5002C11.8879 22.1905 11.3206 22.7502 10.6207 22.7502C9.92081 22.7502 9.35345 22.1905 9.35345 21.5002C9.35345 19.8426 8.68589 18.2528 7.49762 17.0807C6.30935 15.9086 4.69771 15.2502 3.01724 15.2502C2.31736 15.2502 1.75 14.6905 1.75 14.0002C1.75 13.3098 2.31736 12.7502 3.01724 12.7502C4.69771 12.7502 6.30935 12.0917 7.49762 10.9196C8.68589 9.74748 9.35345 8.15777 9.35345 6.50016C9.35345 5.80981 9.92081 5.25016 10.6207 5.25016Z" fill="url(#paint0_linear_25008_3889)" data-sentry-element="path" data-sentry-source-file="GradientStrokeInput.tsx" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M22.0259 17.3335C22.3591 17.3335 22.6293 17.6 22.6293 17.9287C22.6293 18.7181 22.9472 19.4751 23.513 20.0332C24.0789 20.5914 24.8463 20.9049 25.6465 20.9049C25.9798 20.9049 26.25 21.1714 26.25 21.5002C26.25 21.8289 25.9798 22.0954 25.6465 22.0954C24.8463 22.0954 24.0789 22.409 23.513 22.9671C22.9472 23.5253 22.6293 24.2823 22.6293 25.0716C22.6293 25.4003 22.3591 25.6668 22.0259 25.6668C21.6926 25.6668 21.4224 25.4003 21.4224 25.0716C21.4224 24.2823 21.1045 23.5253 20.5387 22.9671C19.9728 22.409 19.2054 22.0954 18.4052 22.0954C18.0719 22.0954 17.8017 21.8289 17.8017 21.5002C17.8017 21.1714 18.0719 20.9049 18.4052 20.9049C19.2054 20.9049 19.9728 20.5914 20.5387 20.0332C21.1045 19.4751 21.4224 18.7181 21.4224 17.9287C21.4224 17.6 21.6926 17.3335 22.0259 17.3335Z" fill="url(#paint1_linear_25008_3889)" data-sentry-element="path" data-sentry-source-file="GradientStrokeInput.tsx" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M22.0259 2.3335C22.3591 2.3335 22.6293 2.59999 22.6293 2.92873C22.6293 3.71807 22.9472 4.47507 23.513 5.03322C24.0789 5.59136 24.8463 5.90492 25.6465 5.90492C25.9798 5.90492 26.25 6.17142 26.25 6.50016C26.25 6.8289 25.9798 7.0954 25.6465 7.0954C24.8463 7.0954 24.0789 7.40896 23.513 7.96711C22.9472 8.52525 22.6293 9.28226 22.6293 10.0716C22.6293 10.4003 22.3591 10.6668 22.0259 10.6668C21.6926 10.6668 21.4224 10.4003 21.4224 10.0716C21.4224 9.28226 21.1045 8.52525 20.5387 7.96711C19.9728 7.40896 19.2054 7.0954 18.4052 7.0954C18.0719 7.0954 17.8017 6.8289 17.8017 6.50016C17.8017 6.17142 18.0719 5.90492 18.4052 5.90492C19.2054 5.90492 19.9728 5.59136 20.5387 5.03322C21.1045 4.47507 21.4224 3.71807 21.4224 2.92873C21.4224 2.59999 21.6926 2.3335 22.0259 2.3335Z" fill="url(#paint2_linear_25008_3889)" data-sentry-element="path" data-sentry-source-file="GradientStrokeInput.tsx" />
      <defs data-sentry-element="defs" data-sentry-source-file="GradientStrokeInput.tsx">
        <linearGradient id="paint0_linear_25008_3889" x1="26.25" y1="14.0002" x2="1.75" y2="14.0002" gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="GradientStrokeInput.tsx">
          <stop stop-color="#AF70FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
          <stop offset="1" stop-color="#3DA9FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
        </linearGradient>
        <linearGradient id="paint1_linear_25008_3889" x1="26.25" y1="14.0002" x2="1.75" y2="14.0002" gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="GradientStrokeInput.tsx">
          <stop stop-color="#AF70FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
          <stop offset="1" stop-color="#3DA9FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
        </linearGradient>
        <linearGradient id="paint2_linear_25008_3889" x1="26.25" y1="14.0002" x2="1.75" y2="14.0002" gradientUnits="userSpaceOnUse" data-sentry-element="linearGradient" data-sentry-source-file="GradientStrokeInput.tsx">
          <stop stop-color="#AF70FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
          <stop offset="1" stop-color="#3DA9FF" data-sentry-element="stop" data-sentry-source-file="GradientStrokeInput.tsx" />
        </linearGradient>
      </defs>
    </svg>;
};